import React from 'react';
import cn from 'classnames';

import { Typography } from 'components/shared/Typography';
import { Collapsible } from 'components/shared/Collapsible';
import { Icon } from 'components/shared/Icon';
import { Checkbox } from 'components/shared/Checkbox';
import { NotificationCard } from 'components/NotificationCard';

import { getTranslation } from 'helpers/getTranslation';

import { EmptyProperties } from '../EmptyProperties';
import { PrototypeProperties } from '../PrototypeProperties';
import { getIconNameByValue } from '../CategoryCheckbox/CategoryCheckbox.helpers';
import { getReactionsCategoryCheckValue } from '../PrototypeProperties/PrototypeProperties.helpers';

import PagePrototypeMeta from './PagePrototypeMeta';

import styles from './PrototypeSection.module.scss';

const PrototypeSection = ({
  isInitialState,
  readOnly,
  designType,
  properties,
  namePrefix,
  flowStartingPoints = [],
  prototypeStartNode = null,
  taskStatePrefix = '',
  onInteractionsCategoryCheckChanged,
  onReactionPropertyChanged,
  onFlowCategoryCheckChanged,
  onStartNodeCategoryCheckChanged,
}: {
  isInitialState: any;
  readOnly: any;
  designType: string;
  properties: any;
  namePrefix: string;
  flowStartingPoints?: Array<{
    name: string;
    nodeName: string | null;
    checks?: { name?: boolean; nodeName?: boolean };
  }>;
  prototypeStartNode?: {
    name: string;
    type: string;
    checks?: { name?: boolean; type?: boolean };
  } | null;
  taskStatePrefix?: string;
  onInteractionsCategoryCheckChanged: (checked: boolean) => void;
  onReactionPropertyChanged: (name: string, checked: boolean) => void;
  onFlowCategoryCheckChanged: (checked: boolean) => void;
  onStartNodeCategoryCheckChanged: (checked: boolean) => void;
}) => {
  const interactions = properties.find(
    (property: any) => property.key === 'reactions',
  );
  const reactionsValue = Array.isArray(interactions?.value)
    ? interactions.value
    : [];
  const hasInteractions = reactionsValue.length > 0;
  const hasPageMeta =
    flowStartingPoints.length > 0 || Boolean(prototypeStartNode);
  const showInteractionsSection = isInitialState ? hasInteractions : true;
  const checkable = !isInitialState;
  const interactionsCategoryCheckValue =
    getReactionsCategoryCheckValue(reactionsValue);

  if (!showInteractionsSection && !hasPageMeta) {
    return (
      <div className={styles.section}>
        {!isInitialState && (
          <NotificationCard
            className={styles.notificationCard}
            variant="info"
            text={getTranslation(
              'figma_lesson_page.main_settings.prototype_section.notification_text',
            )}
          />
        )}
        <EmptyProperties />
      </div>
    );
  }

  return (
    <>
      {!isInitialState && (
        <NotificationCard
          className={styles.notificationCard}
          variant="info"
          text={getTranslation(
            'figma_lesson_page.main_settings.prototype_section.notification_text',
          )}
        />
      )}
      <PagePrototypeMeta
        isInitialState={isInitialState}
        readOnly={readOnly}
        designType={designType}
        taskStatePrefix={taskStatePrefix}
        flowStartingPoints={flowStartingPoints}
        prototypeStartNode={prototypeStartNode}
        onFlowCategoryCheckChanged={onFlowCategoryCheckChanged}
        onStartNodeCategoryCheckChanged={onStartNodeCategoryCheckChanged}
      />
      {showInteractionsSection ? (
        <Collapsible
          transitionTime={150}
          triggerTagName="div"
          className={styles.section}
          openedClassName={cn(styles.section, styles.sectionOpened)}
          open
          trigger={({ onTriggerClick }: { onTriggerClick: any }) => {
            return (
              <div className={styles.header}>
                {checkable && (
                  <Checkbox
                    className={styles.check}
                    checked={
                      interactionsCategoryCheckValue === 'checked' ||
                      interactionsCategoryCheckValue === 'partiallyChecked'
                    }
                    icon={getIconNameByValue(interactionsCategoryCheckValue)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      event.stopPropagation();
                      onInteractionsCategoryCheckChanged(event.target.checked);
                    }}
                  />
                )}
                <div className={styles.headerInner} onClick={onTriggerClick}>
                  <Typography
                    className={styles.title}
                    variant="body2"
                    weight="medium"
                    mode="compact"
                  >
                    Interactions
                  </Typography>
                  <Icon className={styles.icon} name="chevronDown" />
                </div>
              </div>
            );
          }}
        >
          <div className={styles.inner}>
            {hasInteractions ? (
              <PrototypeProperties
                isInitialState={isInitialState}
                readOnly={readOnly}
                designType={designType}
                namePrefix={namePrefix}
                properties={properties}
                onPropertyChanged={onReactionPropertyChanged}
              />
            ) : (
              <Typography
                className={styles.emptyInteractionsText}
                variant="body2"
                mode="compact"
              >
                {getTranslation(
                  'figma_lesson_page.main_settings.prototype_section.empty_interactions_text',
                )}
              </Typography>
            )}
          </div>
        </Collapsible>
      ) : null}
    </>
  );
};

export default React.memo(PrototypeSection) as any;
